/**
 * Composable for PAN (Permanent Account Number) utility functions.
 * Add all PAN-related helpers here.
 *
 * @module usePanUtilities
 */

export function usePanUtilities({
  formData,
  formState,
  isBusinessStepCompleted,
}) {
  /**
   * Validates an Indian PAN number.
   * Format: 5 uppercase letters, 4 digits, 1 uppercase letter (e.g., ABCDE1234F)
   * @param {string} pan - The PAN number to validate
   * @returns {boolean} True if valid, false otherwise
   */
  const { getPanDetail } = useOrganisationStore();
  const { user } = storeToRefs(useUserStore());
  const { organisation } = storeToRefs(useOrganisationStore());
  const panApiFailed = ref(false);
  const panLoading = ref(false);
  const gstinList = ref([]);
  const gstOptionsFilter = ref({
    entities: [],
    onFilter: (query, update, abort) => {
      abort();
    },
    onFilterAbort: () => {},
  });
  const { queryParams } = useRouterUtilities();
  const hasFetchedPanDetails = ref(false);

  const computedPanErrorMessage = computed(() => {
    if (panApiFailed.value) return "No business found with this PAN";
    else return "";
  });

  const isPanDetailsDisabled = computed(() => {
    if (queryParams.value?.add) return true;

    const pan = formData.value.pan || "";
    const isPanValid = /^[A-Z]{5}\d{4}[A-Z]$/.test(pan);

    return (
      pan.length !== 10 ||
      !isPanValid ||
      (unref(formState).edit && unref(isBusinessStepCompleted)) ||
      panLoading.value ||
      computedGstUnregistered.value ||
      isBusinessStepCompleted.value
    );
  });

  async function fetchPanDetail() {
    try {
      panLoading.value = true;

      panApiFailed.value = false;

      const response = await getPanDetail(unref(formData).pan, {
        params: { org: true, role: unref(user).role },
      });

      gstinList.value = (response || [])
        .filter((item) => item.status === "ACTIVE")
        .map((item) => ({
          ...item,
          disable: !!item.is_onboarded,
        }));

      gstOptionsFilter.value = useFilter({
        entities: gstinList,
        props: ["gstin"],
      });

      formData.value.gstinList = response;
    } catch (error) {
      panApiFailed.value = true;

      console.error(error);
    } finally {
      panLoading.value = false;
    }
  }

  const computedGstUnregistered = computed({
    get: () => !formData.value.is_gst_registered,
    set: (isGstRegistered) => {
      formData.value.is_gst_registered = !isGstRegistered;

      panApiFailed.value = false;

      if (isGstRegistered) {
        const director = formData.value.directors[0];

        director.name = user?.value?.basicInfo?.name ?? "";

        director.phone = user?.value?.basicInfo?.phone ?? "";
      }
    },
  });

  const handlePanUpdate = (pan) => {
    formData.value.gstin = "";

    formData.value.pan = pan.trim().toUpperCase();

    panApiFailed.value = false;
  };

  watch([queryParams, formData], () => {
    const isAddMode = queryParams.value?.add;
    const isEditMode = formState.value.edit;
    const isGstRegistered = unref(formData).is_gst_registered;
    const panExists = !!formData.value.pan;

    if (
      isAddMode &&
      panExists &&
      !hasFetchedPanDetails.value &&
      isGstRegistered &&
      !isEditMode
    ) {
      hasFetchedPanDetails.value = true;

      fetchPanDetail();
    } else if (isAddMode && !isGstRegistered && !isEditMode) {
      const org = unref(organisation) || {};

      formData.value._id = org._id ?? null;

      formData.value.internal_number = org.internal_number ?? null;
    }
  });

  return {
    fetchPanDetail,
    handlePanUpdate,
    panApiFailed,
    panLoading,
    gstOptionsFilter,
    computedPanErrorMessage,
    isPanDetailsDisabled,
    computedGstUnregistered,
  };
}
